import React, { useEffect, useRef, useState } from "react";
import styles from "./Careers.module.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faLocationDot,
  faBriefcase,
} from "@fortawesome/free-solid-svg-icons";

import HeroSection from "../../sections/HeroSection/HeroSection";
import FAQSection from "../../sections/FAQSection/FAQSection";
import { useNavigate } from "react-router-dom";

import aboutBg from "../../assets/DimoraImages/IMG_9826.jpg";

import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";

import { CAREERS_PAGE_CONTENT } from "../../Constants";

const Careers = () => {
  const sectionRef = useRef(null);
  const navigate = useNavigate();

  const [faqData, setFaqData] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  /* ================= Animation Logic ================= */
  useEffect(() => {
    const elements =
      sectionRef.current.querySelectorAll("[data-animate]");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.show);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.15,
        rootMargin: "0px 0px -60px 0px",
      }
    );

    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [jobs]);

  /* ================= Fetch FAQs & Jobs ================= */
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch FAQs
        const faqSnapshot = await getDocs(
          collection(db, "faqs", "career", "items")
        );

        const items = faqSnapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => (a.order || 0) - (b.order || 0));

        if (items.length > 0) {
          setFaqData([
            {
              section: "Careers Related Questions",
              items: items.map((item) => ({
                question: item.question,
                answer: item.answer,
              })),
            },
          ]);
        }

        // Fetch Jobs
        const jobsSnapshot = await getDocs(collection(db, "jobs"));
        const allJobs = jobsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const openJobs = allJobs
          .filter((j) => j.status === "open")
          .sort((a, b) => {
            const dateA = a.createdAt?.toMillis
              ? a.createdAt.toMillis()
              : 0;
            const dateB = b.createdAt?.toMillis
              ? b.createdAt.toMillis()
              : 0;
            return dateB - dateA;
          });

        setJobs(openJobs);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div ref={sectionRef} className={styles.careers}>
      <HeroSection backgroundImage={aboutBg} />

      {/* ================= SECTION 1 ================= */}
      <section className={styles.loveSection}>
        <div
          data-animate
          className={`${styles.loveLeft} ${styles.animate} ${styles.fadeLeft}`}
        >
          <h1>
            {CAREERS_PAGE_CONTENT.loveSection.headingLine1} <br />
            {CAREERS_PAGE_CONTENT.loveSection.headingLine2}
          </h1>

          <p>
            {CAREERS_PAGE_CONTENT.loveSection.description}
          </p>

          <ul>
            {CAREERS_PAGE_CONTENT.loveSection.benefits.map(
              (benefit, index) => (
                <li key={index}>
                  <span className={styles.iconCircle}>
                    <FontAwesomeIcon icon={faCheck} />
                  </span>
                  {benefit}
                </li>
              )
            )}
          </ul>
        </div>

        <div
          data-animate
          className={`${styles.loveRight} ${styles.animate} ${styles.fadeRight} ${styles.delay1}`}
        >
          <img
            src="https://pub-dc5215774265416a9110e4c88aef9f9d.r2.dev/imps/IMG_1637.jpg"
            alt="career"
          />
        </div>
      </section>

      {/* ================= SECTION 2 ================= */}
      <section className={styles.saySection}>
        <div
          data-animate
          className={`${styles.sayImage} ${styles.animate} ${styles.fadeLeft}`}
        >
          <img
            src="https://pub-dc5215774265416a9110e4c88aef9f9d.r2.dev/imps/IMG_1642.jpg"
            alt="testimonial"
          />
        </div>

        <div
          data-animate
          className={`${styles.sayContent} ${styles.animate} ${styles.fadeRight} ${styles.delay1}`}
        >
          <h2>
            {CAREERS_PAGE_CONTENT.testimonialSection.heading}
          </h2>

          <p>
            {CAREERS_PAGE_CONTENT.testimonialSection.quote}
          </p>

          <p>
            {CAREERS_PAGE_CONTENT.testimonialSection.author}
          </p>
        </div>
      </section>

      {/* ================= SECTION 3 ================= */}
      <section className={styles.openings}>
        <div
          data-animate
          className={`${styles.openingsHeader} ${styles.animate} ${styles.fadeUp}`}
        >
          <div className={styles.line}></div>
          <h2>
            {CAREERS_PAGE_CONTENT.openingsSection.heading}
          </h2>
          <p>
            {CAREERS_PAGE_CONTENT.openingsSection.description}
          </p>
        </div>

        {loading ? (
          <p
            style={{
              textAlign: "center",
              color: "#e6a15a",
              marginTop: "20px",
            }}
          >
            {CAREERS_PAGE_CONTENT.openingsSection.loadingText}
          </p>
        ) : jobs.length > 0 ? (
          jobs.map((job, index) => (
            <div
              key={job.id}
              data-animate
              className={`${styles.jobCard} ${styles.animate} ${styles.fadeUp} ${
                index % 2 !== 0 ? styles.delay1 : ""
              }`}
            >
              <div className={styles.cardInner}>
                <h3>{job.role?.toUpperCase()}</h3>

                <div className={styles.jobMeta}>
                  <span>
                    <FontAwesomeIcon icon={faLocationDot} />{" "}
                    {CAREERS_PAGE_CONTENT.openingsSection.location}
                  </span>

                  <span>
                    <FontAwesomeIcon icon={faBriefcase} />{" "}
                    {job.type}
                  </span>
                </div>

                <p>
                  {job.description?.substring(0, 120)}...
                </p>
              </div>

              <button
                onClick={() =>
                  navigate(`/career-overview/${job.id}`)
                }
              >
                {CAREERS_PAGE_CONTENT.openingsSection.applyButton}
              </button>
            </div>
          ))
        ) : (
          <p
            style={{
              textAlign: "center",
              color: "#777",
              marginTop: "20px",
            }}
          >
            {CAREERS_PAGE_CONTENT.openingsSection.emptyText}
          </p>
        )}
      </section>

      <FAQSection faqData={faqData} />
    </div>
  );
};

export default Careers;